// Wraps an area page's content and enforces the area grant BEFORE rendering it.
// Proactive gate: reads the caller's areas (GET /api/admin/permissions/me) and
// shows the access-required panel when the area isn't granted. `AreaBoundary`
// is the per-query safety net: a page's own data fetch may still 403 (the
// backend re-checks the grant), which we render as access-required rather than a
// generic error.
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

import { ApiError } from "../lib/api";
import { AREA_META, type Area, hasArea } from "../lib/areas";
import { myAreasQuery } from "../lib/queries";
import { resolveSignInReason } from "../lib/sign-in-reason";
import { AccessRequired } from "./access-required";
import { SignInPanel } from "./sign-in-panel";
import { ErrorState, LoadingState } from "./states";

/** True when a refusal is about IDENTITY, not about an area grant. The employee
 *  gate runs before every area gate, so its 403 can surface on any page query
 *  (an admin cookie that lapsed while a client one lingers). Rendering that as
 *  "you need the X area" sends the operator to ask for a grant they already
 *  hold, instead of to the sign-in button that fixes it. */
function isIdentityRefusal(error: ApiError): boolean {
  return error.code === "admin_signin_required" || error.code === "not_employee";
}

/**
 * @param area  The area required, or a list of which ANY ONE suffices.
 *
 * A list is for a screen that legitimately serves more than one role. The
 * Explorer is the case: `explorer` opens it in full mode, `customer_support` in
 * support mode reading a narrower payload. Holding either is enough to render;
 * WHICH data it then fetches is decided by `useExplorerMode`, not here.
 *
 * The access-required panel names the FIRST area listed — the primary one to
 * ask for — rather than reciting alternatives at someone who has none of them.
 */
export function AreaGate({
  area,
  children,
}: {
  area: Area | readonly Area[];
  children: ReactNode;
}) {
  const areas = useQuery(myAreasQuery);
  const required = typeof area === "string" ? [area] : area;

  if (areas.isPending) return <LoadingState />;
  if (areas.isError) {
    if (
      areas.error instanceof ApiError &&
      (areas.error.status === 401 || isIdentityRefusal(areas.error))
    ) {
      return <SignInPanel reason={resolveSignInReason(areas.error.status, areas.error.code)} />;
    }
    return <ErrorState error={areas.error} onRetry={() => void areas.refetch()} />;
  }
  // LEVEL-BLIND: holding an area read-only opens its page. What a read-only
  // holder does NOT get is the controls on it, and each page decides that from
  // the same granted set (`canWrite`). Gating the page itself on the level would
  // dark a screen someone is entitled to read.
  if (!required.some((key) => hasArea(areas.data, key))) {
    return <AccessRequired area={AREA_META[required[0]]} />;
  }
  return <>{children}</>;
}

/** Map an area-page query error to the right panel: an identity refusal (401, or
 *  a 403 from the employee gate) → sign-in, any other 403 → access-required (the
 *  grant was revoked between the gate and the fetch), else a generic retryable
 *  error. Identity is checked FIRST: both arrive as 403 and only the code tells
 *  them apart. */
export function AreaBoundary({
  area,
  error,
  onRetry,
}: {
  area: Area;
  error: unknown;
  onRetry?: () => void;
}) {
  if (error instanceof ApiError) {
    if (error.status === 401 || isIdentityRefusal(error)) {
      return <SignInPanel reason={resolveSignInReason(error.status, error.code)} />;
    }
    if (error.status === 403) return <AccessRequired area={AREA_META[area]} />;
  }
  return <ErrorState error={error} onRetry={onRetry} />;
}
